import React from 'react';
import {useTranslation} from 'react-i18next';
import {indirizzoImmagine} from '../../utils/immagini';

/**
 * LA FASCIA DEI FORNITORI: i loghi (o i nomi) in fila, che scorrono come una
 * riga di giochi. Col dito e col trackpad scorre da se'; col mouse si
 * trascina (tieni premuto e sposta), e un trascinamento non apre il
 * fornitore su cui si lascia il tasto.
 *
 * La usano la Home (blocco Fornitori: toccando si va alla lobby filtrata),
 * la lobby di Casino' e Live casino e la pagina di una categoria della Home
 * (in cima, al posto delle categorie: toccando si filtra la pagina aperta
 * per quel fornitore, toccando di nuovo si toglie il filtro).
 *
 * `marquee`: in Home e nelle pagine di categoria la fascia SCORRE DA SOLA
 * di continuo (useMarquee): le voci si raddoppiano e lo scorrimento
 * avanza un po' a ogni quadro, sullo stesso elemento su cui si scorre a
 * mano, cosi' rotella, trackpad e trascinamento continuano a funzionare. Al
 * passaggio del mouse, col fuoco su un logo, e per un attimo dopo che si e'
 * scorso a mano, il movimento si ferma; intanto un riflesso di luce passa
 * sui riquadri (Casino.css). Chi ha chiesto meno animazioni non la vede
 * muoversi. Sul telefono la fascia della lobby e delle pagine di categoria
 * non si vede (Casino.css): al suo posto c'e' il tasto che apre il cassetto
 * (CassettoFornitori.js); quella della Home si', e scorre da sola anche li'.
 *
 * `versoDestraAlTelefono`: sotto i 1024px i loghi vanno da sinistra verso
 * destra invece che al contrario (la lobby del guscio Nova); dal computer il
 * verso resta quello di sempre.
 *
 * `voci`: [{nome, immagine}]. `scelto`: il nome del fornitore acceso, se c'e'.
 * `velocita` (pixel al secondo) e `altezza` (pixel del riquadro) vengono
 * dalla skin; senza, valgono VELOCITA e la misura di serie di Casino.css.
 */
export default function FasciaFornitori({voci, scelto, onScegli, etichetta, className, marquee, velocita, altezza, versoDestraAlTelefono}) {
  const {t} = useTranslation();
  const fila = React.useRef(null);
  const trascina = useTrascina(fila);
  const scorre = useMarquee(fila, !!marquee, voci.length, velocita, !!versoDestraAlTelefono);
  const alta = Number(altezza) > 0 ? {'--fornitori-altezza': Number(altezza) + 'px'} : undefined;
  /* Con la fascia che scorre da sola le voci si vedono due volte di fila:
     quando la prima copia e' passata si riparte da capo senza salti. La
     copia non si legge due volte e non si raggiunge col tasto Tab. */
  const lista = scorre.doppia ? voci.concat(voci) : voci;
  return (
    <div className={'cas-fornitori' + (marquee ? ' cas-marquee' : '') + (className ? ' ' + className : '')} ref={fila} role={'list'}
      aria-label={etichetta || t('Fornitori')} style={alta} {...trascina} {...scorre.eventi}>
      {lista.map((v, n) => {
        const copia = n >= voci.length;
        const acceso = !!scelto && v.nome === scelto;
        return (
          <div role={copia ? 'presentation' : 'listitem'} key={(v.nome || '') + n} className={'cas-fornitori-voce'} aria-hidden={copia ? 'true' : undefined}
            style={{'--i': n % voci.length}}>
            <button type={'button'} className={'cas-fornitori-logo' + (acceso ? ' scelto' : '')}
              onClick={() => onScegli(v)} title={v.nome} aria-label={v.nome || t('Fornitore')} tabIndex={copia ? -1 : undefined}
              aria-pressed={scelto === undefined || copia ? undefined : acceso} draggable={false}>
              {v.immagine ? <img src={indirizzoImmagine(v.immagine)} alt={''} draggable={false} loading={'lazy'}/> : <span>{v.nome}</span>}
            </button>
          </div>
        );
      })}
    </div>
  );
}

/** Quanti pixel al secondo avanza la fascia da sola. */
const VELOCITA = 36;
/** Quanto resta ferma dopo una rotella o un tocco, in millisecondi. */
const RIPRESA = 2200;

/** Vero sotto i 1024px (il telefono), se `attivo`; segue la finestra che cambia misura. */
export function useAlTelefono(attivo) {
  const domanda = '(max-width: 1023px)';
  const leggi = () => attivo && typeof window.matchMedia === 'function' && window.matchMedia(domanda).matches;
  const [si, setSi] = React.useState(leggi);
  React.useEffect(() => {
    if (!attivo || typeof window.matchMedia !== 'function') { setSi(false); return undefined; }
    const mq = window.matchMedia(domanda);
    const cambia = () => setSi(mq.matches);
    cambia();
    if (mq.addEventListener) mq.addEventListener('change', cambia);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', cambia); };
  }, [attivo]);
  return si;
}

/**
 * Lo scorrimento automatico di una fila che scorre di lato (il marquee).
 *
 * Restituisce {doppia, eventi}: `doppia` dice se le voci vanno raddoppiate
 * (solo quando la fila esce dallo schermo: se ci sta tutta non c'e' niente
 * da far scorrere), `eventi` vanno sull'elemento. Lo scorrimento si fa con
 * scrollLeft, che il browser arrotonda al pixel: la posizione vera si tiene
 * a parte, se no a pochi pixel al secondo la fascia non partirebbe mai.
 * Quando lo scorrimento supera la prima copia si torna indietro di un giro
 * (e viceversa, se si trascina all'indietro oltre l'inizio).
 */
export function useMarquee(ref, acceso, quante, velocita, versoDestraAlTelefono) {
  const pxSecondo = Number(velocita) > 0 ? Number(velocita) : VELOCITA;
  const aDestra = useAlTelefono(!!versoDestraAlTelefono);
  const [doppia, setDoppia] = React.useState(false);
  const fermo = React.useRef({mouse: false, fuoco: false, mano: 0});
  const posizione = React.useRef(0);
  const ultima = React.useRef(-1);

  /* Raddoppiare solo se la fila esce dallo schermo: si misura la prima copia.
     Nascosta (sul telefono, display: none) e' larga zero e non si raddoppia. */
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!acceso || !el || !quante) { setDoppia(false); return undefined; }
    const misura = () => {
      const primo = el.children[0];
      const ultimoDellaPrima = el.children[Math.min(quante, el.children.length) - 1];
      if (!primo || !ultimoDellaPrima) { setDoppia(false); return; }
      const larghezzaPrima = ultimoDellaPrima.offsetLeft + ultimoDellaPrima.offsetWidth - primo.offsetLeft;
      setDoppia(el.clientWidth > 0 && larghezzaPrima > el.clientWidth + 1);
    };
    misura();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', misura);
      return () => window.removeEventListener('resize', misura);
    }
    const oss = new ResizeObserver(misura);
    oss.observe(el);
    return () => oss.disconnect();
  }, [ref, acceso, quante]);

  React.useEffect(() => {
    const el = ref.current;
    if (!acceso || !doppia || !el) return undefined;
    const menoMovimento = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (menoMovimento) return undefined;
    let quadro = 0;
    let prima = 0;
    const passo = (ora) => {
      quadro = window.requestAnimationFrame(passo);
      const dt = prima ? Math.min(ora - prima, 100) : 0;
      prima = ora;
      const f = fermo.current;
      /* Mentre si trascina col mouse si sta fermi, e per un attimo anche dopo. */
      if (el.classList.contains('trascina')) f.mano = ora + RIPRESA;
      if (f.mouse || f.fuoco || f.mano > ora) { ultima.current = -1; return; }
      const giro = el.children[quante] ? el.children[quante].offsetLeft - el.children[0].offsetLeft : 0;
      if (giro <= 0) return;
      /* Qualcuno ha scorso a mano (rotella, trackpad, trascinamento): si riparte da dove ha lasciato. */
      if (ultima.current < 0 || Math.abs(el.scrollLeft - ultima.current) > 1) posizione.current = el.scrollLeft;
      posizione.current += ((aDestra ? -1 : 1) * pxSecondo * dt) / 1000;
      if (posizione.current >= giro) posizione.current -= giro;
      if (posizione.current < 0) posizione.current += giro;
      const nuova = Math.round(posizione.current);
      if (nuova !== el.scrollLeft) el.scrollLeft = nuova;
      ultima.current = el.scrollLeft;
    };
    quadro = window.requestAnimationFrame(passo);
    return () => window.cancelAnimationFrame(quadro);
  }, [ref, acceso, doppia, quante, pxSecondo, aDestra]);

  const aMano = () => { fermo.current.mano = performance.now() + RIPRESA; };
  return {
    doppia,
    eventi: acceso ? {
      onPointerEnter: (e) => { if (e.pointerType === 'mouse') fermo.current.mouse = true; },
      onPointerLeave: (e) => { if (e.pointerType === 'mouse') fermo.current.mouse = false; },
      onFocus: () => { fermo.current.fuoco = true; },
      onBlur: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) fermo.current.fuoco = false; },
      onWheel: aMano,
      onTouchStart: aMano,
      onTouchMove: aMano,
    } : {},
  };
}

/**
 * Trascinare col mouse una fila che scorre di lato. Il dito e la penna no:
 * per loro il browser scorre gia' da solo. Oltre pochi pixel e' un
 * trascinamento, e il clic che segue si butta.
 */
export function useTrascina(ref) {
  const stato = React.useRef(null);
  const appenaTrascinato = React.useRef(false);
  return {
    onPointerDown: (e) => {
      const el = ref.current;
      if (e.pointerType !== 'mouse' || e.button !== 0 || !el || el.scrollWidth <= el.clientWidth) return;
      stato.current = {x: e.clientX, inizio: el.scrollLeft, mosso: false, id: e.pointerId};
      appenaTrascinato.current = false;
    },
    onPointerMove: (e) => {
      const s = stato.current;
      const el = ref.current;
      if (!s || !el) return;
      const dx = e.clientX - s.x;
      if (!s.mosso && Math.abs(dx) < 6) return;
      if (!s.mosso) {
        s.mosso = true;
        el.classList.add('trascina');
        if (el.setPointerCapture) { try { el.setPointerCapture(s.id); } catch (_) { /* gia' rilasciato */ } }
      }
      el.scrollLeft = s.inizio - dx;
    },
    onPointerUp: () => fine(),
    onPointerCancel: () => fine(),
    onClickCapture: (e) => {
      if (appenaTrascinato.current) {
        appenaTrascinato.current = false;
        e.preventDefault();
        e.stopPropagation();
      }
    },
  };
  function fine() {
    const s = stato.current;
    stato.current = null;
    if (!s || !s.mosso) return;
    appenaTrascinato.current = true;
    if (ref.current) ref.current.classList.remove('trascina');
  }
}
